import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { lastValueFrom, takeUntil } from 'rxjs';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TranslateService } from '@ngx-translate/core';
import { DialogConfirmComponent } from '@src/app/shared/dialogs';
import { DocumentService } from '@src/core/services';
import { BaseResizableComponent } from '@src/app/modules/base';
import { isFileContact } from '@src/utils';

import { PageInfoMode, PageInfoType } from './types';
import { PageInfoService } from './services';

@Component({
  selector: 'app-page-info',
  templateUrl: './page-info.component.html',
  styleUrls: ['./page-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageInfoComponent extends BaseResizableComponent implements OnChanges {
  private readonly pageInfoService = inject(PageInfoService);
  private readonly translateService = inject(TranslateService);
  private readonly documentService = inject(DocumentService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);

  @Input() mode: PageInfoMode = 'view';
  @Output() modeChange = new EventEmitter<PageInfoMode>();
  @Input() pageId?: string;
  @Output() saved = new EventEmitter<string>();
  @Output() deleted = new EventEmitter<void>();

  loading = false;

  readonly data$ = this.pageInfoService.pageInfo$;

  private readonly confirmCancelEditingDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.undoEditHeader'),
      size: 's',
      closeable: false,
    },
  );

  private readonly confirmDeletingDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.deleteHeader'),
      size: 's',
      closeable: false,
    },
  );

  constructor() {
    super();

    this.data$.pipe(takeUntil(this.destroyed$$)).subscribe(pageInfo => {
      this.loading = !pageInfo;
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageId']) {
      this.pageInfoService.resetPageInfo();
      if (!!this.pageId) {
        this.setMode('view');
        this.pageInfoService.loadPageInfo(this.pageId);
      }
    }

    if (changes.mode) {
      if (this.mode === 'create') {
        this.pageInfoService.resetPageInfo();
        this.loading = false;
      }
    }

    this.cdr.markForCheck();
  }

  onStartEditing() {
    this.setMode('edit');
  }

  onDelete(pageId: string): void {
    this.confirmDeletingDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: res => {
        if (res) {
          this.loading = true;
          this.pageInfoService.deletePageInfo(pageId).subscribe(() => {
            this.pageInfoService.resetPageInfo();
            this.setMode('none');
            this.deleted.emit();
          });
        }
      },
    });
  }

  onCancel(): void {
    this.confirmCancelEditingDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: res => {
        if (res) {
          if (this.pageId) {
            this.pageInfoService.resetPageInfo();
            this.pageInfoService.loadPageInfo(this.pageId);
            this.setMode('view');
          } else {
            this.setMode('none');
          }
        }
      },
    });
  }

  async onSaveData(data: PageInfoType): Promise<void> {
    this.loading = true;

    if (data.contacts) {
      const promises = data.contacts.map(async contact => {
        if (isFileContact(contact.contactTypeId)) {
          if (contact.oldDocument?.id) {
            contact.contact = contact.oldDocument.id;
          }

          if (contact.newDocument) {
            contact.contact = await this.uploadDocument(contact.newDocument, 10); // 10 Документ
          }

          contact.oldDocument = undefined;
          contact.newDocument = undefined;
        }
        return contact;
      });
      await Promise.all(promises);
    }

    if (this.mode === 'edit') {
      this.updatePageInfo(data);
    } else if (this.mode === 'create') {
      this.createPageInfo(data);
    }
  }

  private setMode(mode: PageInfoMode) {
    this.mode = mode;
    this.modeChange.emit(mode);
  }

  private createPageInfo(data: PageInfoType): void {
    this.pageInfoService
      .createPageInfo(data)
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(async res => {
        const pageInfoId = res.id;
        if (pageInfoId) {
          this.pageInfoService.loadPageInfo(pageInfoId);
        }

        this.saved.emit(pageInfoId);

        this.setMode('view');
        this.cdr.markForCheck();
      });
  }

  private updatePageInfo(data: PageInfoType): void {
    this.pageInfoService
      .editPageInfo(data)
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(() => {
        const pageInfoId = data.id;
        if (pageInfoId) {
          this.pageInfoService.loadPageInfo(pageInfoId);
        }

        this.saved.emit(pageInfoId);

        this.setMode('view');
        this.cdr.markForCheck();
      });
  }

  private async uploadDocument(document: File, documentType?: number, attachId?: string): Promise<string | undefined> {
    if (!document) return;

    const documentList = await lastValueFrom(this.documentService.addDocuments([document], attachId, documentType));

    return documentList?.files?.[0].id;
  }
}
